import dayjs from 'dayjs';
import {
  DASHBOARD_DATE_FORMATS,
  DashboardDateAdapter,
} from 'src/app/dashboard/ui/components/dashboard-datepicker/dashboard-date-adapter';

import type { OnDestroy, OnInit } from '@angular/core';
import { Component, inject, input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription } from 'rxjs';
import type { Observable } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { DataService } from 'src/app/dashboard/services/data.service';
import { RefreshEventService } from 'src/app/dashboard/services/refresh-event.service';

import type { DateFilterForm } from './date-filter-form';
import { DateFilterFormBuilder } from './date-filter-form';

const browserLang =
  (window.navigator.languages && window.navigator.languages[0]) ||
  window.navigator.language;

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
  ],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: browserLang.indexOf('ja') != -1 ? 'ja-JP' : 'en-US',
    },
    {
      provide: DateAdapter,
      useClass: DashboardDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: DASHBOARD_DATE_FORMATS,
    },
  ],
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
})
export class DateFilterComponent implements OnInit, OnDestroy {
  // Inject services
  private readonly dataService = inject(DataService);
  private readonly refreshEventService = inject(RefreshEventService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(DateFilterFormBuilder);

  readonly componentId = input<string>(
    Math.random().toString(36).substring(2, 9),
  ); // 自分からの更新通知を識別するために使うID

  private subscription = new Subscription();
  private isLocalUpdate = false;

  // 翻訳済みテキスト
  labels = {
    title: '',
    start: '',
    end: '',
    search: '',
    alertMessage: '',
  };

  get filterCaption$(): Observable<string> {
    return this.translate.get('dashboard.Span', {
      days: this.form.diffDays(),
    });
  }

  form: DateFilterForm;
  maxDate: Date = new Date(); // 今日まで

  constructor() {
    this.form = this.formBuilder.build();
  }

  ngOnInit(): void {
    this.initLabels();
    // console.log('DateFilterComponent initialized', this.componentId); // デバッグ用

    // 初期値を設定
    const currentRange = this.dataService.getCurrentDateRange();
    if (currentRange.start && currentRange.end) {
      this.form.dateRangeForm.setValue({
        start: currentRange.start,
        end: currentRange.end,
      });
    }

    // 日付範囲の変更を購読
    this.subscription.add(
      this.dataService.dateRange$.subscribe((dateRange) => {
        if (dateRange.start && dateRange.end) {
          if (!this.isLocalUpdate) {
            // console.log('updateDateRange', dateRange);
            this.form.dateRangeForm.setValue({
              start: dateRange.start,
              end: dateRange.end,
            });
          }
          this.isLocalUpdate = false;
        }
      }),
    );

    // 更新元IDを監視して、自分自身からの更新を無視
    this.subscription.add(
      this.dataService.updateSourceId$.subscribe((sourceId) => {
        this.isLocalUpdate = sourceId === this.componentId();
      }),
    );
  }

  onClick() {
    // ボタンがクリックされたら、フォームの値を取得して共有
    if (!this.form.isInvalid()) {
      const formValues = this.form.dateRangeForm.value;

      // サービスに日付範囲を通知（自分のIDも渡す）
      this.dataService.updateDateRange(
        {
          start: formValues.start,
          end: formValues.end,
        },
        this.componentId(),
      );

      const startDate = this.form.getStartDate();
      const endDate = this.form.getEndDate();

      if (startDate && endDate) {
        const startDateStr = dayjs(startDate).format('YYYY-MM-DD');
        const endDateStr = dayjs(endDate).format('YYYY-MM-DD');

        // データサービスの日付範囲を更新
        this.dataService.setDateRange(startDateStr, endDateStr);

        // URLのクエリパラメータを更新
        this.updateQueryParams(startDateStr, endDateStr);

        // データの更新をトリガー
        this.refreshEventService.triggerRefresh();
      }
    }
  }

  ngOnDestroy() {
    // サブスクリプションの解除
    this.subscription.unsubscribe();
  }

  private initLabels(): void {
    this.translate
      .get([
        'dateFilter.title',
        'dateFilter.startDate',
        'dateFilter.endDate',
        'dateFilter.search',
        'dashboard.Alert',
      ])
      .subscribe((translations) => {
        this.labels.title = translations['dateFilter.title'];
        this.labels.start = translations['dateFilter.startDate'];
        this.labels.end = translations['dateFilter.endDate'];
        this.labels.search = translations['dateFilter.search'];
        this.labels.alertMessage = translations['dashboard.Alert'];
      });
  }

  private updateQueryParams(startDate: string, endDate: string): void {
    const queryParams = {
      date_s: startDate.replaceAll('-', ''),
      date_e: endDate.replaceAll('-', ''),
    };

    this.router.navigate([], {
      relativeTo: this.route,
      fragment: this.route.snapshot.fragment ?? '',
      queryParams: queryParams,
      queryParamsHandling: 'merge', // 他のクエリパラメータを保持
    });
  }

  isInvalidSearchPeriod(): boolean {
    return this.form.isInvalidSearchPeriod();
  }
}
